<template>
  <button
    v-bind="$attrs"
    :class="[
      'selection-card',
      {
        'selection-card--selected': selected,
        'selection-card--disabled': disabled,
      },
    ]"
    :aria-pressed="selected ? 'true' : 'false'"
    :disabled="disabled"
    type="button"
    @click="handleClick"
  >
    <span class="selection-card__content">
      <span
        v-if="hasSlot['leading']"
        class="selection-card__leading"
      >
        <slot name="leading" />
      </span>

      <span class="selection-card__text">
        <slot name="title">
          <span class="kds-typography-title-medium">
            {{ title }}
          </span>
        </slot>

        <slot name="description">
          <span
            v-if="description"
            class="kds-typography-interactive-small kds-color-text-on-blue"
          >
            {{ description }}
          </span>
        </slot>
      </span>
    </span>

    <span
      v-if="hasSlot['trailing']"
      class="selection-card__trailing"
    >
      <slot name="trailing" />
    </span>
  </button>
</template>

<script setup>
import useVueSlots from '@shared/composables/useVueSlots.js'

defineOptions({
  inheritAttrs: false,
})

const { hasSlot } = useVueSlots(['leading', 'trailing'])

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  selected: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['click'])

const handleClick = (event) => {
  if (!props.disabled) {
    emit('click', event)
  }
}
</script>

<style lang="scss" scoped>
.selection-card {
  width: 100%;
  @include flex-container(row, space-between, md, center);
  padding: $kds-space-1_5x $kds-space-2_5x;
  border: 0;
  border-radius: $kds-space-1x;
  box-shadow: inset 0 0 0 1.5px $kds-color-background-strong;
  background-color: $kds-color-background-subtle;
  color: $kds-color-text-primary;
  cursor: pointer;
  text-align: left;
  transition:
    background-color 0.2s ease,
    box-shadow 0.2s ease;

  &:focus-visible {
    outline: $kds-color-line-focus solid 1.5px;
    box-shadow: inset 0 0 0 1.5px $kds-color-line-focus;
  }

  @media (hover: hover) {
    &:hover:not(:disabled) {
      background-color: $kds-color-background-strong;
    }
  }

  &--selected {
    box-shadow: inset 0 0 0 3px $kds-color-background-interactive-accent;
  }

  &--disabled {
    background-color: $kds-color-background-interactive-disabled;
    color: $kds-color-text-on-interactive-disabled;
    cursor: not-allowed;
  }

  &__content {
    @include flex-container(row, flex-start, md, center);
  }

  &__leading {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  &__text {
    min-width: 0;
    @include flex-container(column, flex-start, xs, flex-start);
  }

  &__trailing {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  &--disabled &__leading,
  &--disabled &__description,
  &--disabled &__trailing {
    color: $kds-color-text-on-interactive-disabled;
  }
}
</style>
